import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faEnvelope, faLock, faUser, faCheck } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';

const CustomerPortalSignup: React.FC = () => {
  const { inviteToken } = useParams<{ inviteToken: string }>();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeToAlerts: false,
    allowLocation: false
  });
  
  const [inviteData, setInviteData] = useState<any>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);

  useEffect(() => {
    // Validate invite token
    validateInviteToken();
  }, [inviteToken]);

  const validateInviteToken = async () => {
    try {
      const response = await axiosInstance.get(`/customer-portal/auth/validate-invite/${inviteToken}`);
      if (response.data.success) {
        setInviteData(response.data.data);
        setFormData(prev => ({
          ...prev,
          email: response.data.data.buyerEmail || ''
        }));
      } else {
        setError('Invalid or expired invitation link');
      }
    } catch (err) {
      setError('Invalid or expired invitation link');
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await axiosInstance.post('/customer-portal/auth/signup', {
        ...formData,
        inviteToken
      });
      
      if (response.data.success) {
        localStorage.setItem('customerPortalToken', response.data.token);
        navigate('/customer-portal/portal');
      } else {
        setError(response.data.message || 'Signup failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'An error occurred during signup');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <Container className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-3">Validating invitation...</p>
        </div>
      </Container>
    );
  }

  if (!inviteData && !validating) {
    return (
      <Container className="min-vh-100 d-flex align-items-center justify-content-center">
        <Card>
          <Card.Body className="text-center p-5">
            <h4>Invalid Invitation</h4>
            <p className="text-muted">This invitation link is invalid or has expired.</p>
            <p>Please contact your agent for a new invitation.</p>
            <Button variant="primary" href="/customer-portal/login">
              Go to Login
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="min-vh-100 d-flex align-items-center justify-content-center py-5">
      <Row className="w-100">
        <Col md={6} lg={5} className="mx-auto">
          <Card className="shadow">
            <Card.Body className="p-5">
              <div className="text-center mb-4">
                <FontAwesomeIcon icon={faHome} size="3x" className="text-primary mb-3" />
                <h2>Welcome, {inviteData?.buyerName?.split(' ')[0]}!</h2>
                <p className="text-muted">
                  Your agent {inviteData?.agentName} has invited you to find your dream home
                </p>
              </div>

              {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name</Form.Label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <FontAwesomeIcon icon={faUser} />
                    </span>
                    <Form.Control
                      type="text"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <FontAwesomeIcon icon={faEnvelope} />
                    </span>
                    <Form.Control
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      readOnly
                      className="bg-light"
                    />
                  </div>
                  <Form.Text className="text-muted">
                    <FontAwesomeIcon icon={faCheck} className="text-success me-1" />
                    Verified by your agent
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Password</Form.Label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <FontAwesomeIcon icon={faLock} />
                    </span>
                    <Form.Control
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      minLength={8}
                    />
                  </div>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Confirm Password</Form.Label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <FontAwesomeIcon icon={faLock} />
                    </span>
                    <Form.Control
                      type="password"
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      required
                      minLength={8}
                    />
                  </div>
                </Form.Group>

                <div className="mb-4">
                  <Form.Check
                    type="checkbox"
                    id="alerts"
                    label="Get alerts for new matches and price drops"
                    checked={formData.agreeToAlerts}
                    onChange={(e) => setFormData({ ...formData, agreeToAlerts: e.target.checked })}
                  />
                  <Form.Check
                    type="checkbox"
                    id="location"
                    label="Allow location for better search results"
                    checked={formData.allowLocation}
                    onChange={(e) => setFormData({ ...formData, allowLocation: e.target.checked })}
                  />
                </div>

                <Button
                  variant="primary"
                  type="submit"
                  className="w-100"
                  size="lg"
                  disabled={loading}
                >
                  {loading ? 'Creating Account...' : 'Continue to Portal →'}
                </Button>
              </Form>

              <hr className="my-4" />

              <div className="text-center">
                <p className="mb-0">
                  Already have an account?
                  <a href="/customer-portal/login" className="text-decoration-none ms-1">
                    Sign In
                  </a>
                </p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default CustomerPortalSignup;